import { useState } from "react";
import i18n from "i18next";
import { useTranslation } from "react-i18next";
import { useAppSelector, useAppDispatch } from "../app/hooks";
import {
  selectProject,
  selectPattern,
  setName,
  setPatternName,
  setSelectedPattern,
  setScale,
  setImageUrl,
  invertImage,
  setBrightnessImage,
  addPattern,
  duplicatePattern,
  RemovePattern,
  setGuideLength,
  selectGuides,
} from "./patternSlice";
import { setScaleMode, selectSetScaleMode } from "./toolsSlice";
import BackgroundInput from "./BackgroundInput";
import { ControlsWrapper } from "./ControlsWrapper";
import Parameters from "./Parameters";
import { ClosedCategories, loadCosedCategories } from "../utils/serialization";
import { Category } from "./CategoryWrapper";
import ScaleInput from "./ScaleInput";

interface ControlsProps {
  onNewAction: Function;
  onSaveAction: Function;
  onLoadAction: Function;
  onPdfAction: Function;
  gcode: string;
  onGcodeAction: Function;
  svg: string;
  onSvgAction: Function;
}

export default function Controls({
  onNewAction,
  onSaveAction,
  onLoadAction,
  onPdfAction,
  gcode,
  onGcodeAction,
  svg,
  onSvgAction,
}: ControlsProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [closedCategories, SetClosedCategories] = useState<ClosedCategories>(
    loadCosedCategories()
  );
  const project = useAppSelector(selectProject);
  const pattern = useAppSelector(selectPattern);
  const guides = useAppSelector(selectGuides);
  const scaleMode = useAppSelector(selectSetScaleMode);

  const guideInputs = guides.map(({ lengthExpresion, label }, i) => {
    if (i < 2) {
      //Don't display origins
      return null;
    }
    return (
      <div key={i}>
        <div>{label || i - 1 /*start counting at 1*/}</div>
        <div>
          <input
            type="text"
            value={lengthExpresion}
            onChange={(e) => {
              const value = {
                length: e.target.value,
                index: i,
              };
              dispatch(setGuideLength(value));
            }}
          />
        </div>
      </div>
    );
  });

  const PATTERN_NAME_PREFIX = "PATTERN:";
  const patternNames = [
    "",
    ...Object.keys(window.localStorage)
      .filter((name) => {
        return !name.indexOf(PATTERN_NAME_PREFIX);
      })
      .map((name) => name.replace(PATTERN_NAME_PREFIX, ""))
      .sort(),
  ];

  //hardcoded samples. Move them to a db later
  const sample1Label = "tank top - sample";
  if (!window.localStorage.getItem(sample1Label)) {
    patternNames.push(sample1Label);
  }
  const sample2Label = "briefs - sample";
  if (!window.localStorage.getItem(sample2Label)) {
    patternNames.push(sample2Label);
  }
  ///

  const options = patternNames.map((name) => (
    <option key={name} value={name}>
      {name}
    </option>
  ));

  return (
    <ControlsWrapper className="controls">
      <Category
        name="Language"
        closedCategories={closedCategories}
        onClick={SetClosedCategories}
      >
        <select
          value={i18n.language}
          onChange={(e) => i18n.changeLanguage(e.currentTarget.value)}
          style={{ width: "unset" }}
        >
          <option value="en">English</option>
          <option value="es">Español</option>
        </select>
      </Category>

      <Category
        name="Project"
        closedCategories={closedCategories}
        onClick={SetClosedCategories}
      >
        <div>
          <div>
            <input
              type="button"
              value={t("New pattern") as string}
              onClick={() => onNewAction()}
            />
          </div>
          <div>
            <input
              value={t("Save project") as string}
              type="button"
              onClick={() => onSaveAction()}
            />
          </div>
        </div>

        <div>
          <div>{t("Patterns")}</div>
          <div>
            <select
              value={project.projectName}
              onChange={(e) => onLoadAction(e.target.value)}
            >
              {options}
            </select>
          </div>
        </div>

        <div>
          <div>{t("New name")}</div>
          <div>
            <input
              type="text"
              value={project.projectName}
              onChange={(e) => dispatch(setName(e.target.value))}
            />
          </div>

          <div>{t("Sections")}</div>
          <div>
            <select
              value={project.selectedPattern}
              onChange={(e) =>
                dispatch(setSelectedPattern(Number.parseInt(e.target.value)))
              }
            >
              {project.patterns.map((pattern, i) => {
                return (
                  <option key={i} value={i}>
                    {pattern.name}
                  </option>
                );
              })}
            </select>
          </div>

          <div>
            <button
              style={{ marginRight: 2 }}
              onClick={() => {
                dispatch(addPattern());
              }}
            >
              {t("Add section")}
            </button>
            <button
              style={{ marginRight: 2 }}
              onClick={() => {
                dispatch(duplicatePattern());
              }}
            >
              {t("Duplicate section")}
            </button>
            <button
              onClick={() => {
                dispatch(RemovePattern());
              }}
            >
              {t("Remove section")}
            </button>
          </div>

          <div>{t("Section name")}</div>
          <div>
            <input
              type="text"
              value={pattern.name}
              onChange={(e) => dispatch(setPatternName(e.target.value))}
            />
          </div>
        </div>
      </Category>

      <Category
        name="Background"
        closedCategories={closedCategories}
        onClick={SetClosedCategories}
      >
        <div>
          <div>{t("Background image url")}</div>
          <div>
            <input
              type="text"
              value={project.imageUrl}
              onChange={(e) => {
                const value = e.target.value;
                dispatch(setImageUrl(value));
              }}
            />
          </div>
        </div>

        <BackgroundInput
          onChange={(url) => {
            dispatch(setImageUrl(url));
          }}
        />

        <div>
          <div>
            {t("Invert background colors")}
            <input
              type="checkbox"
              checked={project.invertImage}
              onChange={(e) => dispatch(invertImage())}
            />
          </div>
          <div>
            {t("Brightness")}
            <input
              type="range"
              min="0.1"
              max="1"
              step=".1"
              value={project.brightnessImage || 1}
              onChange={(e) =>
                dispatch(setBrightnessImage(e.currentTarget.valueAsNumber))
              }
            />
          </div>
        </div>
      </Category>

      <Category
        name="Parameters"
        closedCategories={closedCategories}
        onClick={SetClosedCategories}
      >
        <div>
          <div>{t("Scale")}</div>
          <div>
            <ScaleInput
              value={project.scale}
              onChange={(value: number) => dispatch(setScale(value))}
            />
          </div>
          <div>
            <input
              type="button"
              value={t("Set scale") as string}
              onClick={() => dispatch(setScaleMode())}
              style={{
                backgroundColor: scaleMode ? "black" : undefined,
                color: scaleMode ? "white" : undefined,
              }}
            />
          </div>
        </div>

        <Parameters />
      </Category>

      <Category
        name="Guides"
        closedCategories={closedCategories}
        onClick={SetClosedCategories}
      >
        {guideInputs}
      </Category>

      <Category
        name="Export"
        closedCategories={closedCategories}
        onClick={SetClosedCategories}
      >
        <div>
          <div>PDF</div>
          <div>
            <input
              type="button"
              value={t("Print") as string}
              onClick={() => onPdfAction()}
            />
          </div>
        </div>

        <div>
          <div>svg</div>
          <div>
            <input
              type="button"
              value={t("Generate") as string}
              onClick={() => onSvgAction()}
            />
          </div>
          <textarea
            value={svg}
            readOnly={true}
            onClick={(e) => (e.target as HTMLTextAreaElement).select()}
          />
        </div>

        <div>
          <div>{t("gcode")}</div>
          <div>
            <input
              type="button"
              value={t("Generate") as string}
              onClick={() => onGcodeAction()}
            />
          </div>
          <textarea
            value={gcode}
            readOnly={true}
            onClick={(e) => (e.target as HTMLTextAreaElement).select()}
          />
        </div>
      </Category>
    </ControlsWrapper>
  );
}
